import React, { useEffect, useState } from 'react';
import axios from 'axios';
import VideoPlayer from '../Content/VideoPlayer';
import { server } from '../../server';
import { getStoredAuthToken } from '../../utils/authSession';

const getCurrentProfile = () => sessionStorage.getItem('currentProfile') || 'User';

const getAuthHeaders = () => {
  const token = getStoredAuthToken();
  const headers = { 'current-profile': getCurrentProfile() };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
};

const RoomMediaGrid = ({ roomId, refreshKey }) => {
  const [mediaItems, setMediaItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let isMounted = true;

    const fetchRoomMedia = async () => {
      if (!roomId) {
        return;
      }

      setLoading(true);
      setError('');

      try {
        const response = await axios.get(`${server}/room/rooms/${roomId}/posts`, {
          withCredentials: true,
          headers: getAuthHeaders(),
        });

        const posts = Array.isArray(response.data.posts) ? response.data.posts : [];
        const nextMediaItems = posts.flatMap((post) => {
          const images = Array.isArray(post.images)
            ? post.images.map((image, index) => ({
                id: image._id || image.key || image.url || `${post._id}-image-${index}`,
                type: 'image',
                url: image.url,
                postText: post.description,
              }))
            : [];

          const videos = Array.isArray(post.videos)
            ? post.videos.map((video, index) => ({
                id: video._id || video.key || video.url || `${post._id}-video-${index}`,
                type: 'video',
                key: video.key,
                url: video.url,
                postText: post.description,
              }))
            : [];

          return [...images, ...videos];
        });

        if (isMounted) {
          setMediaItems(nextMediaItems);
        }
      } catch (err) {
        if (isMounted) {
          setMediaItems([]);
          setError(err.response?.data?.message || 'Unable to load community photos right now.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchRoomMedia();

    return () => {
      isMounted = false;
    };
  }, [roomId, refreshKey]);

  if (loading) {
    return <div className="community-profile-empty-state">Loading community photos...</div>;
  }

  if (error) {
    return <div className="community-profile-empty-state">{error}</div>;
  }

  if (mediaItems.length === 0) {
    return <div className="community-profile-empty-state">No photos or videos yet.</div>;
  }

  return (
    <div className="community-profile-media-grid">
      {mediaItems.map((media) => (
        <div key={media.id} className="community-profile-media-card">
          {media.type === 'image' ? (
            <img src={media.url} alt={media.postText || 'Community media'} />
          ) : media.key ? (
            <VideoPlayer videoUrl={media.key} />
          ) : (
            <video controls>
              <source src={media.url} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          )}
        </div>
      ))}
    </div>
  );
};

export default RoomMediaGrid;
